// DEFAULT, and the only one that never under-applies.
import type { SpreadRxNutrient } from "./spreadRx";

export type BlendBasis = "max" | "avg" | SpreadRxNutrient;

/* * One selected nutrient's per-cell PRODUCT rate (lbs/ac), already converted at its own analysis. */
export interface BlendInput {
  nutrient: SpreadRxNutrient;
  rates: (number | null)[];
}

/* * Resolve the per-cell product rate for a blend. */
export function blendRates(
  inputs: BlendInput[],
  basis: BlendBasis,
  cells: number,
): (number | null)[] {
  if (inputs.length === 0) return new Array(cells).fill(null);
  if (inputs.length === 1) return inputs[0]!.rates.slice(0, cells);

  if (basis !== "max" && basis !== "avg") {
    const one = inputs.find((i) => i.nutrient === basis);
    // A basis naming a nutrient that is no longer selected must not silently fall through to some
    // other rule — the caller keeps the two in sync, and this is the backstop.
    return one ? one.rates.slice(0, cells) : new Array(cells).fill(null);
  }

  const out: (number | null)[] = new Array(cells).fill(null);
  for (let i = 0; i < cells; i++) {
    let sum = 0;
    let n = 0;
    let max = -Infinity;
    for (const inp of inputs) {
      const v = inp.rates[i];
      if (v == null || !Number.isFinite(v)) continue;
      sum += v;
      n += 1;
      if (v > max) max = v;
    }
    if (!n) continue;
    out[i] = basis === "max" ? Math.round(max) : Math.round(sum / n);
  }
  return out;
}

/* * How the basis reads in the UI and in the column name. */
export const BASIS_LABEL: Record<string, string> = {
  max: "Highest of the selected",
  avg: "Average of the selected",
  lime: "Lime target",
  p2o5: "P₂O₅ target",
  k2o: "K₂O target",
};

/* * The token that records the decision in the dBase column name — `CustomMax`, `CustomP2O5`. */
export function basisToken(basis: BlendBasis): string {
  return basis === "max"
    ? "Max"
    : basis === "avg"
      ? "Avg"
      : basis === "lime"
        ? "Lime"
        : basis === "p2o5"
          ? "P2O5"
          : "K2O";
}
